import { FC, useEffect, useState } from 'react';
import { IGetStudentCoupons, IStudentCouponType } from '../../../../../models/coupons2.model';
import { useTranslation } from 'react-i18next';
import useNotification from '../../../../../hooks/useNotification';
import { Button as AntButton, Modal } from 'antd';
import { IStudent } from '../../../../../models/students.model';
import useAxios from '../../../../../hooks/useAxios';
import useLoader from '../../../../../hooks/useLoader';
import { createCoupon, createCouponWithAmount, deleteCoupon } from '../../../../../api/coupons2.api';
import moment from 'moment';
import Radio from '../../../../Radio';
import TabItem from '../../../../class/class/TabItem';
import useUtil from '../../../../../hooks/useUtil';
import Button from '../../../../Button';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  student?: IStudent;
  couponTypes?: IStudentCouponType[];
  coupons?: IGetStudentCoupons;
  doGetStudentCoupons: () => void;
  sendLog: (message: string, object?: object) => void;
}

const IssueCouponModal: FC<IProps> = ({
                                        visible,
                                        setVisible,
                                        student,
                                        couponTypes = [],
                                        coupons,
                                        doGetStudentCoupons,
                                        sendLog,
                                      }) => {
  const { t } = useTranslation();
  const { getCommonCodeLabel } = useUtil();
  const { infoNotification, successNotification } = useNotification();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();
  useLoader(submitIsLoading, [submitError]);

  const [tab, setTab] = useState<string>('ISSUE');
  const [couponCode, setCouponCode] = useState<number>();
  const [memo, setMemo] = useState('');
  const [amount, setAmount] = useState('');
  const [showAmountInput, setShowAmountInput] = useState(false);

  useEffect(() => {
    if (submitRes) {
      successNotification(t('student.student.message40'));
      doGetStudentCoupons();
    }
  }, [submitRes]);

  useEffect(() => {
    if (deleteRes) {
      successNotification(t('common.message.delete'));
      doGetStudentCoupons();
    }
  }, [deleteRes]);

  const onSubmit = () => {
    if (!student) {
      return;
    }
    if (!couponCode) {
      return infoNotification(t('student.student.message41'));
    }
    if (showAmountInput && !amount) {
      return infoNotification(t('student.student.message42'));
    }

    if (amount) {
      submitExecute(createCouponWithAmount(
        couponCode,
        student.studentCode,
        { amount: Number(amount), memo },
      ));
      sendLog(
        'createCouponWithAmount',
        {
          couponCode,
          studentCode: student.studentCode,
          amount: Number(amount),
          memo,
        },
      );
    } else {
      submitExecute(createCoupon(
        couponCode,
        student.studentCode,
        { memo },
      ));
      sendLog(
        'createCoupon',
        {
          couponCode,
          studentCode: student.studentCode,
          memo,
        },
      );
    }
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSubmit();
    }
  };

  const onDeleteCoupon = (couponStudentCode: number) => {
    deleteExecute(deleteCoupon(couponStudentCode));
    sendLog('deleteCoupon', { couponStudentCode });
  };

  const tabs = [
    {
      menu: 'ISSUE',
      menus: 'ISSUE',
      text: '쿠폰 발급',
    },
    {
      menu: 'LIST',
      menus: 'LIST',
      text: '쿠폰 목록',
    },
  ];

  return (
    <Modal
      title="쿠폰 관리"
      visible={visible}
      onCancel={() => setVisible(false)}
      width={600}
      footer={[
        <>
          {tab === 'ISSUE' && (
            <div className="comm-btn__area justify-content-center">
              <AntButton
                key="submit"
                className="btn-basic pink"
                onClick={onSubmit}
              >
                {t('student.student.button20')}
              </AntButton>
              <AntButton
                key="cancel"
                className="btn-basic gray"
                onClick={() => setVisible(false)}
              >
                {t('common.button.cancel')}
              </AntButton>
            </div>
          )}
        </>,
      ]}
    >
      <div className="comm-tab__line">
        <ul>
          {tabs.map(tabItem => (
            <TabItem
              tab={tab}
              setTab={setTab}
              menu={tabItem.menu}
              menus={tabItem.menus}
              text={tabItem.text}
            />
          ))}
        </ul>
      </div>

      {tab === 'ISSUE' && (
        <div>
          <table className="table table-bordered">
            <tbody>
            <tr>
              <th className="text-center">
                {t('student.student.column99')}
              </th>
              <th className="text-center">
                {t('student.student.column100')}
              </th>
              <th className="text-center">
                {t('student.student.column101')}
              </th>
              <th className="text-center">
                {t('student.student.column102')}
              </th>
            </tr>
            {couponTypes.map(item => (
              <tr key={item.couponCode}>
                <td className="text-center">
                  {item.couponName}
                </td>
                <td className="text-center">
                  {
                    item.amount
                      ? item.amount.toLocaleString()
                      : '-'
                  }
                </td>
                <td className="text-center">
                  {moment(item.createEndDate).format('YYYY.MM.DD')}
                </td>
                <td className="text-center">
                  <Radio
                    checked={item.couponCode === couponCode}
                    onChange={() => {
                      setCouponCode(item.couponCode);
                      setAmount('');
                      if (item.amount) {
                        setShowAmountInput(false);
                      } else {
                        setShowAmountInput(true);
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
            </tbody>
          </table>
          {showAmountInput && (
            <div className="flex flex-justify-center mb-2">
              <div className="mx-2 v-line">
                {t('student.student.column97')}
              </div>
              <input
                type="text"
                className="form-control input-text w-240p"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                onKeyPress={onKeyPress}
              />
            </div>
          )}
          <div className="flex flex-justify-center">
            <div className="mx-2 v-line">
              {t('common.text.note')}
            </div>
            <input
              type="text"
              className="form-control input-text w-240p"
              value={memo}
              onChange={e => setMemo(e.target.value)}
              onKeyPress={onKeyPress}
            />
          </div>
        </div>
      )}

      {tab === 'LIST' && (
        <div>
          <table className="table table-bordered">
            <tbody>
            <tr>
              <th className="text-center">
                {t('student.student.column99')}
              </th>
              <th className="text-center">
                대상
              </th>
              <th className="text-center">
                {t('student.student.column100')}
              </th>
              <th className="text-center">
                {t('common.button.delete')}
              </th>
            </tr>

            {coupons?.data?.filter(item => item.useYn !== 'Y').map(item => (
              <tr key={item.couponCode}>
                <td className="text-center">
                  {item.couponName}
                </td>

                <td className="text-center">
                  {getCommonCodeLabel('PA0001', item.paymentTargetCode?.trim())}
                </td>

                <td className="text-center">
                  {item.amount}
                </td>
                <td className="text-center">
                  <div className="comm-btn__area justify-content-center">
                    <Button
                      className="btn-basic small gray"
                      onClick={() => onDeleteCoupon(item.couponStudentCode)}
                      text={t('common.button.delete')}
                      disabled={item.useYn === 'Y'}
                    />
                  </div>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

export default IssueCouponModal;